/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useCallback, useEffect, useState } from 'react';

import { JobsMonitorTable } from '@/pages/JobsPage/JobsMonitorPage/JobsMonitorTable';
import { DataTableFilterMeta } from 'primereact/datatable';
import { Utils } from '@/utils/utils.ts';
import { AppRoutes } from '@/constants/routes.ts';
import { useNavigate } from 'react-router';
import { processCommander, useQueryFilters } from '@/hooks/useQueryFilters.tsx';
import { MonitorProcessJobs } from '@/types/models';
import { useMutation } from '@tanstack/react-query';

import { useProcessesContext } from 'ogc-processes-components';
import { AntflowApiEndpoint } from '@/services/dataprovider';
import { useInterval } from 'primereact/hooks';
import { RowProcessInfo } from './JobsMonitorPage/JobsMonitorTable/type';
import { jobsMonitorContext } from './JobsMonitorPage/JobsMonitorTable/JobsMonitorContext';

const REFRESH_TIME = 60000;

export const JobsMonitorPage: React.FC = () => {
  const navigate = useNavigate();

  const { processesEndpoint } = useProcessesContext();

  const { prefilters } = useQueryFilters({ commander: processCommander });

  const [monitorData, setMonitorData] = useState<MonitorProcessJobs>();

  const [autoRefresh, setAutoRefresh] = useState<boolean>(false);

  const [processesInfoData, setProcessesInfoData] = useState<RowProcessInfo[]>();

  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  const {
    isPending,
    mutateAsync: getMonitorData,
    reset
  } = useMutation({
    mutationKey: ['get_monitor'],
    mutationFn: async (query?: {
      page?: number;
      limit?: number;
      runs?: number;
      querySearchText?: string | undefined;
    }) => {
      const endpoint = processesEndpoint as AntflowApiEndpoint;
      let page = 1,
        limit = 10,
        runs = 40;
      if (query) {
        page = query.page ?? page;
        limit = query.limit ?? limit;
        runs = query.runs ?? runs;
      }
      const queryParams: Record<string, string> = {
        limit: limit.toString(),
        page: page.toString(),
        runs: runs.toString()
      };
      if (query?.querySearchText) {
        queryParams['query'] = query.querySearchText;
      }
      const response = await endpoint.get_jobs_monitor(queryParams);

      setMonitorData(response);
      return response;
    }
  });

  const updateFilters = useCallback(
    (filters: DataTableFilterMeta, options: { deferred?: boolean; resetSearch?: boolean }) => {
      const queriesParams: Record<string, string> = Utils.filtersToQueryParams(filters);
      const empty = queriesParams.global === undefined || queriesParams.global === '';
      const fn = () => {
        navigate(AppRoutes.jobs.list.path + Utils.buildWithQueryParamsUrl(queriesParams));
        if (options.resetSearch || empty) {
          reset();
          getMonitorData({});
        }
      };
      if (options.deferred) {
        setTimeout(() => {
          fn();
        }, 1200);
        return;
      }
      fn();
    },
    [navigate, getMonitorData, reset]
  );

  useEffect(() => {
    const searchText = prefilters?.global as string | undefined;
    const query: Record<string, string> = {};
    if (searchText !== undefined && searchText.length > 2) {
      setIsLoaded(true);
      reset();
      query['querySearchText'] = searchText;
    }
    getMonitorData(query);
  }, [getMonitorData, prefilters, reset]);

  useInterval(
    () => {
      setAutoRefresh(true);
      getMonitorData({
        limit: monitorData?.pagination.limit,
        page: monitorData?.pagination.page,
        querySearchText: prefilters?.global as string | undefined
      }).then(() => {
        setAutoRefresh(false);
      });
    },
    REFRESH_TIME,
    !isPending && !autoRefresh
  );

  const updateRowState = (rowDataProcessInfo: RowProcessInfo) => {
    setProcessesInfoData((prevState) => {
      const _prevState = prevState ?? [];
      if (_prevState) {
        const prevRowTargeted = _prevState.filter((el) => el.processId !== rowDataProcessInfo.processId);
        return [...prevRowTargeted, rowDataProcessInfo];
      }
      return prevState;
    });
  };

  useEffect(() => {
    if (monitorData?.processes) {
      monitorData.processes.map((rowData) => {
        const { project_path, id, commit_ref } = rowData;
        const endpoint = processesEndpoint as AntflowApiEndpoint;
        const query = { project_path, project_ref: commit_ref, process_id: id };

        if (project_path && commit_ref) {
          const rowDataProcessInfo: RowProcessInfo = {
            processId: id,
            fecthStatus: 'pending'
          };
          updateRowState(rowDataProcessInfo);
          endpoint
            .get_job_monitor_process_info(query)
            .then((response) => {
              if (response) {
                updateRowState({ ...rowDataProcessInfo, fecthStatus: 'sucessfull', data: response });
              }
            })
            .catch(() => {
              updateRowState({ ...rowDataProcessInfo, fecthStatus: 'failed' });
            });
        } else {
          updateRowState({ processId: id, fecthStatus: 'failed' });
        }
      });
    }
  }, [monitorData, processesEndpoint]);

  return (
    <div>
      <jobsMonitorContext.Provider
        value={{
          processesInfoData
        }}
      >
        <JobsMonitorTable
          loading={isPending && !autoRefresh}
          queryParamIsLoaded={isLoaded}
          monitorData={monitorData}
          getMonitorData={(query) => {
            reset();
            return getMonitorData({
              ...query
            });
          }}
          defaultFilters={prefilters}
          handleGlobalFilters={(filters) => updateFilters(filters, { deferred: true })}
          onClearFilters={{
            command: (filters) => updateFilters(filters, { resetSearch: true })
          }}
        />
      </jobsMonitorContext.Provider>
    </div>
  );
};
